import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MarkdownModule } from 'ngx-markdown';
import { ButtonModule } from 'primeng/button';

import { ConversationMessage } from 'src/app/@core/services/ai/ai-assistant.service';
import { DocLinkTarget } from 'src/app/@shared/components/documentation/documentation-content/documentation-content.component';
import { MessageComponent } from 'src/app/@shared/components/message/message.component';
import { AssistantFeedbackChange, AssistantFeedbackComponent } from '../assistant-feedback/assistant-feedback.component';
import { AssistantActionCardComponent } from '../assistant-action-card/assistant-action-card.component';
import { AssistantUsageComponent } from '../assistant-usage/assistant-usage.component';

/** Ce qu'« Annuler cet échange » n'a pas pu défaire, affiché sous les cartes du tour. */
export interface TurnUndoReport {
    turnIndex: number;
    lines: string[];
}

/** Schéma d'une citation du manuel : `doc:<id-article>[#<ancre>]`. */
const DOC_SCHEME = 'doc:';

/** Préfixe posé par la sanitisation d'Angular sur un schéma d'URL qu'elle ignore. */
const UNSAFE_PREFIX = 'unsafe:';

/** Rendu du fil de conversation (messages + indicateur d'activité « Réflexion »). */
@Component({
    selector: 'app-assistant-conversation',
    standalone: true,
    imports: [
        CommonModule,
        MarkdownModule,
        ButtonModule,
        MessageComponent,
        AssistantActionCardComponent,
        AssistantFeedbackComponent,
        AssistantUsageComponent,
    ],
    templateUrl: './assistant-conversation.component.html',
    styleUrl: './assistant-conversation.component.scss',
})
export class AssistantConversationComponent {

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    @Input() messages: ConversationMessage[] = [];
    @Input() statusText: string | null = null;
    /** Consommation du tour : masquée là où l'assistant ne se décompte pas. */
    @Input() showUsage: boolean = true;
    /** Tour dont l'annulation est en cours : son bouton attend la réponse. */
    @Input() pendingTurnIndex: number | null = null;
    /** Ce que la dernière annulation de tour n'a pas pu défaire. */
    @Input() turnUndoReport: TurnUndoReport | null = null;

    /** Conversation affichée : l'avis sur une réponse se dépose sur l'un de ses tours. */
    @Input() sessionId: string | null = null;
    /** Conversation d'un autre utilisateur (supervision) : les avis s'affichent sans se modifier. */
    @Input() feedbackReadonly: boolean = false;

    /** Citation du manuel suivie par le lecteur : à l'hôte de décider ce qu'il en fait. */
    @Output() openDoc = new EventEmitter<DocLinkTarget>();
    /** « Annuler cet échange » : l'index du tour, à l'hôte de confirmer et d'appeler le serveur. */
    @Output() undoTurn = new EventEmitter<number>();
    /** Avis déposé, remplacé ou retiré sur la réponse d'un tour : à l'hôte de le reporter dans le fil. */
    @Output() feedbackChange = new EventEmitter<{ turnIndex: number } & AssistantFeedbackChange>();

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    /**
     * « Annuler cet échange » se lit à l'affichage : le drapeau du tour ne s'allume jamais de
     * nouveau, et chaque carte, instance partagée mise à jour par ses gestes, dit s'il reste
     * quelque chose à défaire (#1274).
     */
    public canUndoTurn(message: ConversationMessage): boolean {
        return message.turnIndex !== undefined && !!message.canUndoTurn && this._hasUndoableCard(message);
    }

    public onChangeFeedback(message: ConversationMessage, change: AssistantFeedbackChange): void {
        this.feedbackChange.emit({ turnIndex: message.turnIndex!, ...change });
    }

    public onClickThread(event: MouseEvent): void {
        const link = (event.target as HTMLElement)?.closest?.('a') as HTMLAnchorElement | null;
        const href = link?.getAttribute('href') ?? '';
        const target = this._parseDocLink(href);
        if (!target) {
            if (this._isExternalLink(href)) {
                // Source d'une recherche sur internet : jamais dans l'onglet de l'application
                event.preventDefault();
                window.open(href, '_blank', 'noopener');
            }
            return;
        }
        event.preventDefault();
        this.openDoc.emit(target);
    }

    public onClickUndoTurn(message: ConversationMessage): void {
        this.undoTurn.emit(message.turnIndex!);
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /** Une proposition encore refusable, ou une écriture appliquée que l'utilisateur peut annuler. */
    private _hasUndoableCard(message: ConversationMessage): boolean {
        return !!message.actions?.some(action =>
            (action.proposed && action.canReject)
            || (action.processingStatus === 'applied' && !!action.markerId && action.canUndo)
        );
    }

    /** Adresse web (`http` ou `https`), par opposition à une citation du manuel. */
    private _isExternalLink(href: string): boolean {
        return /^https?:\/\//i.test(href);
    }

    /**
     * Cible d'une citation du manuel, ou `null` si le lien n'en est pas une.
     * Angular ne connaît pas le schéma `doc:` et préfixe l'adresse d'`unsafe:`
     * en assainissant le Markdown rendu : le lien reste cliquable, son adresse
     * est juste à déballer.
     */
    private _parseDocLink(href: string): DocLinkTarget | null {
        const address = href.startsWith(UNSAFE_PREFIX) ? href.slice(UNSAFE_PREFIX.length) : href;
        if (!address.startsWith(DOC_SCHEME)) {
            return null;
        }
        const [id, anchor] = address.slice(DOC_SCHEME.length).split('#');
        return id ? { id, anchor: anchor || undefined } : null;
    }
}
